// ============================================================
// UTILITY FUNCTIONS
// Shared helpers used across the iRemedy.ai platform
// ============================================================

// ── CCP Source Label Mapping ─────────────────────────────────
// Maps internal source_key codes to user-friendly display names
export const CCP_SOURCE_LABELS: Record<string, { label: string; category: string }> = {
  DOD_1260H:        { label: "DoD Section 1260H",          category: "Sanctions & Entity Lists" },
  NS_CMIC:          { label: "NS-CMIC (Non-SDN CMC)",      category: "Sanctions & Entity Lists" },
  UFLPA:            { label: "UFLPA Entity List",           category: "Sanctions & Entity Lists" },
  CSL_COMPANIES:    { label: "Consolidated Screening List", category: "Sanctions & Entity Lists" },
  CSL_INDIVIDUALS:  { label: "CSL Individual Screening",    category: "Sanctions & Entity Lists" },
  BIOSECURE:        { label: "BIOSECURE Act",               category: "Sanctions & Entity Lists" },
  PARENT_COMPANY:   { label: "Parent Company Screening",    category: "Sanctions & Entity Lists" },
  GEO_CHINA:        { label: "China (Geographic)",          category: "Ownership & Governance" },
  SOE_ENGLISH:      { label: "State-Owned Enterprise",      category: "Ownership & Governance" },
  CHINESE_REGEX:    { label: "Chinese SOE Identifier",      category: "Ownership & Governance" },
  CNINFO_EQUITY:    { label: "CNINFO Shareholder Data",     category: "Ownership & Governance" },
  OPENSANCTIONS_NPC: { label: "NPC/CPPCC Membership",      category: "Strategic & Political" },
  MIC2025:          { label: "Made in China 2025",          category: "Strategic & Political" },
  SASAC:            { label: "SASAC Directory",             category: "Ownership & Governance" },
  QCC_NAV:          { label: "QCC Navigation",              category: "Ownership & Governance" },
  GROK_CCP:         { label: "Grok CCP Analysis",          category: "Strategic & Political" },
};

export function ccpSourceLabel(sourceKey: string): string {
  return CCP_SOURCE_LABELS[sourceKey]?.label ?? sourceKey.replace(/_/g, " ");
}

export function ccpSourceCategory(sourceKey: string): string {
  return CCP_SOURCE_LABELS[sourceKey]?.category ?? "Other";
}

// ── Chinese-to-Bilingual Summary Mapping ────────────────────
// For evidence cards: translates Chinese names to bilingual "English (中文)" format.
// Covers SOE group names, SASAC holding companies, and common CNINFO entities.
// Ordered longest-first so longer matches take priority (e.g. 华润医药控股有限公司 before 华润).
const CHINESE_TO_ENGLISH: [string, string][] = [
  // CNINFO-specific legal entities (SASAC holding companies / subsidiaries)
  ["华润医药控股有限公司",         "China Resources Pharmaceutical Holdings (华润医药控股有限公司)"],
  ["华润医药商业集团有限公司",     "China Resources Pharma Commercial Group (华润医药商业集团有限公司)"],
  ["中国诚通控股集团有限公司",     "China Chengtong Holdings Group (中国诚通控股集团有限公司)"],
  ["国新投资有限公司",             "China Reform Holdings Investment (国新投资有限公司)"],
  ["中国医药集团有限公司",         "China National Pharmaceutical Group (中国医药集团有限公司)"],
  ["中国医药健康产业股份有限公司", "China Meheco (中国医药健康产业股份有限公司)"],
  ["上海医药集团股份有限公司",     "Shanghai Pharma Group (上海医药集团股份有限公司)"],
  ["中国远大集团有限责任公司",     "China Grand Enterprises (中国远大集团有限责任公司)"],
  // SOE group names
  ["华润集团", "China Resources Group (华润集团)"],
  ["华润",     "China Resources (华润)"],
  ["国药集团", "Sinopharm Group (国药集团)"],
  ["国药",     "Sinopharm (国药)"],
  ["中信集团", "CITIC Group (中信集团)"],
  ["中信",     "CITIC (中信)"],
  ["华北制药", "North China Pharmaceutical (华北制药)"],
  ["上药集团", "Shanghai Pharma Group (上药集团)"],
  ["上海医药", "Shanghai Pharma (上海医药)"],
  ["上药",     "Shanghai Pharma (上药)"],
  ["中国生物", "China National Biotech Group (中国生物)"],
  ["中国医药", "China National Pharmaceutical (中国医药)"],
  ["中国化工", "ChemChina (中国化工)"],
  ["中粮",     "COFCO (中粮)"],
  ["石药集团", "CSPC Pharma Group (石药集团)"],
  ["人福医药", "Humanwell Healthcare (人福医药)"],
  ["鲁抗医药", "Lukang Pharma (鲁抗医药)"],
  ["康恩贝",   "Conba Group (康恩贝)"],
  ["亚宝药业", "Yabao Pharma (亚宝药业)"],
  ["马应龙",   "Mayinglong Pharma (马应龙)"],
  ["汉江",     "Hanjiang Pharma (汉江)"],
  ["威奇达",   "Veyong Pharma (威奇达)"],
  ["海普瑞",   "Hepalink (海普瑞)"],
  ["翰森制药", "Hansoh Pharma (翰森制药)"],
  ["凯莱英",   "Asymchem (凯莱英)"],
  // Known NPC executives appearing in pharma manufacturer evidence
  ["王亮",   "Wang Liang (王亮)"],
  ["钟慧娟", "Zhong Huijuan (钟慧娟)"],
  ["李锂",   "Li Li (李锂)"],
  ["孙飘扬", "Sun Piaoyang (孙飘扬)"],
  // NPC/executive role titles
  ["副总裁", "Vice President (副总裁)"],
  ["总裁",   "President (总裁)"],
  ["董事长", "Chairman (董事长)"],
  ["董事",   "Director (董事)"],
  ["总经理", "General Manager (总经理)"],
];

/**
 * Translate Chinese names in summary text to bilingual "English (中文)" format.
 * Works for both fully-Chinese text and mixed English/Chinese text (e.g. CNINFO data).
 * Uses placeholder tokens to prevent double-replacement of Chinese chars in bilingual output.
 */
export function bilingualSummary(text: string): string {
  if (!text) return text;
  let result = text;
  const placeholders: string[] = [];
  for (const [cn, bilingual] of CHINESE_TO_ENGLISH) {
    if (result.includes(cn)) {
      const token = `\x00${placeholders.length}\x00`;
      placeholders.push(bilingual);
      result = result.split(cn).join(token);
    }
  }
  for (let i = 0; i < placeholders.length; i++) {
    result = result.split(`\x00${i}\x00`).join(placeholders[i]);
  }
  return result;
}

// Clipboard helper that works in HTTP (fallback for non-secure contexts)
export const copyToClipboard = (text: string): Promise<void> => {
  // Try modern clipboard API first (works in HTTPS/localhost)
  if (navigator.clipboard && window.isSecureContext) {
    return navigator.clipboard.writeText(text);
  }
  // Fallback for HTTP: use legacy execCommand
  return new Promise((resolve, reject) => {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.style.position = "fixed";
    textarea.style.left = "-9999px";
    textarea.style.top = "-9999px";
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    try {
      document.execCommand("copy");
      document.body.removeChild(textarea);
      resolve();
    } catch (err) {
      document.body.removeChild(textarea);
      reject(err);
    }
  });
};
